/**
 * System Health Page
 * Monitor system health and error logs
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Activity,
  Database,
  Server,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Clock,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const HealthPage = () => {
  const { api } = useAdmin();
  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorPage, setErrorPage] = useState(1);
  const [totalErrors, setTotalErrors] = useState(0);

  useEffect(() => {
    fetchHealth();
    fetchErrors();
  }, [errorPage]);

  const fetchHealth = async () => {
    try {
      const response = await api.get('/api/admin/health/overview');
      setHealth(response.data);
    } catch (error) {
      toast.error('Failed to load health status');
    } finally {
      setLoading(false);
    }
  };

  const fetchErrors = async () => {
    try {
      const response = await api.get(`/api/admin/health/errors?page=${errorPage}&page_size=20`);
      setErrors(response.data.errors);
      setTotalErrors(response.data.total);
    } catch (error) {
      // Errors collection might not exist
      setErrors([]);
    }
  };

  const refresh = () => {
    setLoading(true);
    fetchHealth();
    fetchErrors();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
          <p className="text-gray-500 text-sm mt-1">Monitor system status and errors</p>
        </div>
        <button
          onClick={refresh}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Round 44: Sign in with Apple keys (used when a member deletes their account) */}
      {health?.apple_signin && (
        <div className={`rounded-xl p-4 border text-sm flex items-start gap-2 ${health.apple_signin.ready ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="health-apple-signin">
          {health.apple_signin.ready ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
          <div>
            <p className="font-semibold">Sign in with Apple keys</p>
            <p>{health.apple_signin.message}</p>
            {(health.apple_signin.missing || []).length > 0 && <p className="text-xs mt-1">Missing: {health.apple_signin.missing.join(', ')}</p>}
          </div>
        </div>
      )}

      {/* Health Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Database Status */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Database</p>
              <div className="flex items-center gap-2 mt-2">
                {health?.database_status === 'healthy' ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <span className="font-semibold text-emerald-600">Healthy</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-rose-500" />
                    <span className="font-semibold text-rose-600">Unhealthy</span>
                  </>
                )}
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Active Streams */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Active Streams</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{health?.active_streams || 0}</p>
            </div>
            <div className="p-3 bg-rose-50 rounded-lg">
              <Activity className="w-6 h-6 text-rose-600" />
            </div>
          </div>
        </div>

        {/* Active Sessions */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{health?.active_sessions || 0}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg">
              <Server className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* Last Updated */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">Last Check</p>
              <p className="text-sm font-medium text-gray-900 mt-2">
                {health?.timestamp ? new Date(health.timestamp).toLocaleTimeString() : 'N/A'}
              </p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* System Overview */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h2 className="font-semibold text-gray-900 mb-4">System Overview</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              {health?.database_status === 'healthy' ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-rose-500" />
              )}
              <span className="font-medium text-gray-900">MongoDB</span>
            </div>
            <p className="text-sm text-gray-500">Primary Database</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="font-medium text-gray-900">API</span>
            </div>
            <p className="text-sm text-gray-500">Backend Server</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="font-medium text-gray-900">WebSocket</span>
            </div>
            <p className="text-sm text-gray-500">Real-time</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="font-medium text-gray-900">Agora</span>
            </div>
            <p className="text-sm text-gray-500">Streaming</p>
          </div>
        </div>
      </div>

      {/* Recent Errors */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-rose-500" />
            Recent Errors
          </h2>
        </div>

        {errors.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-gray-500">No errors logged</p>
            <p className="text-sm text-gray-400 mt-1">System is running smoothly</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {errors.map((error, i) => (
                <div key={i} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900 font-medium truncate">{error.message}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {error.timestamp ? new Date(error.timestamp).toLocaleString() : 'Unknown time'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Total: {totalErrors} errors</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setErrorPage(Math.max(1, errorPage - 1))}
                  disabled={errorPage === 1}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">Page {errorPage}</span>
                <button
                  onClick={() => setErrorPage(errorPage + 1)}
                  className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HealthPage;
